import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft, Link as LinkIcon } from 'lucide-react'
import {
  PiIdentificationCard,
  PiHandshake,
  PiNotepad,
  PiCalendarStar,
  PiHandCoins,
  PiEnvelope,
} from 'react-icons/pi'
import { FaInstagram } from 'react-icons/fa'
import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import { MobileColors } from 'styles/mobileTokens'
import { formatInteger, formatDate2 } from 'utils/formatting'
import { MobileAccountAttributesSummary } from 'components/MobileCompanyPage/MobileAccountAttributes'
import { MobileDealsAccordion } from 'components/MobileCompanyPage/MobileDealsAccordion'
import { MobileEventHistoryAccordion } from 'components/MobileCompanyPage/MobileEventHistory'
import { MobileRemindersAccordion } from 'components/MobileCompanyPage/MobileReminders'
import useDocumentTitle from 'components/useDocumentTitle'
import { useCompanyOrDoorData } from 'hooks/useCompanyOrDoorData'
import { FirstBiteDataDrawer } from 'components/MobileCompanyPage/FirstBiteDataDrawer'
import {
  DescriptionAndContactInfo,
  Divider,
  ForecastingSection,
  MetricCard,
  StatsCard,
  AdditionalCharacteristics,
  SectionWithContent,
  Rating,
} from 'components/NewCompanyPage/Sidebar/shared'
import { AFbLink } from 'components/FbUI/FbLink/styles'
import { PredictedTrafficBadge } from 'components/PredictedTrafficBadge/PredictedTrafficBadge'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { getIdWithPrefix } from 'utils/pathUtils'
import { ContactCompanyType, IContactCompany } from 'models/contact_companies'
import { BackButton } from 'components/Mobile/Mobile.styles'

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #07311e;
  overflow-y: auto;
  padding-bottom: 200px; /* Space for drawer handle */
`

const CompanyHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${MobileColors.backgroundWhite};
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const CompanyHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const CompanyLogoContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid ${MobileColors.borderDefault};
`

const CompanyLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const EmptyLogo = styled.div`
  width: 100%;
  height: 100%;
  background-color: #d9d9d9;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CompanyName = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
  margin: 0;
  flex: 1;
`

const CompanyMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${MobileColors.textSecondary};
`

const MetaDivider = styled.span`
  color: ${MobileColors.borderDefault};
`

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${MobileColors.textSecondary};
  transition: color 0.2s;

  &:hover {
    color: ${MobileColors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ActionButtonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr); /* 6 buttons in one line */
  gap: 4px; /* smaller gap */
  padding: 12px 10px;
  background: ${MobileColors.primary};
  border-bottom: 1px solid #07311e;
`

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px;
  background: ${MobileColors.primaryActive};
  border: none;
  border-radius: 8px;
  margin: 3px;
  color: #ffffff;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  svg {
    font-size: 20px;
    color: #ffffff;
  }

  &:hover {
    background: ${MobileColors.primary};
  }

  &:active {
    background: ${MobileColors.primaryDark};
  }
`

const ActionCount = styled.span`
  font-size: 11px;
  font-weight: 300;
`

const ContentSection = styled.div`
  padding: 12px;
  background: #07311e;
`

const SectionDivider = styled.div`
  height: 8px;
  background: ${MobileColors.backgroundOverlay};
`

export function MobileChainPage() {
  const navigate = useNavigate()
  const urlParams = useParams()
  const { preferences } = usePreferences()
  const chainID = urlParams?.chain_name
    ? getIdWithPrefix(
        urlParams?.chain_name,
        preferences?.company_type_prefix_map[ContactCompanyType.Chain]
      )
    : undefined

  const api = apiService()
  const { data: chainData, isLoading } = api.useGetChain(chainID)

  // Fetch company data for counts and other details
  const { data: companyData } = useCompanyOrDoorData(
    chainData?.contact_company_id || 0
  )

  // Set document title
  useDocumentTitle(`${chainData?.chain || 'Not Found'} (Chain) - First Bite`)

  // Get modified date from company data
  const modifiedDate =
    'modified' in (companyData || {})
      ? (companyData as IContactCompany)?.modified
      : undefined

  // Helper functions for First Bite Data drawer
  const formatHqCity = () => {
    const hqCity1 = chainData?.hq_city1
    const hqState1 = chainData?.hq_state1
    const hqCity2 = chainData?.hq_city2
    const hqState2 = chainData?.hq_state2

    if (hqCity1 && hqState1) {
      return `${hqCity1}, ${hqState1} - USA`
    }
    if (hqCity2 && hqState2) {
      return `${hqCity2}, ${hqState2} - USA`
    }
    if (hqCity1) {
      return `${hqCity1} - USA`
    }
    if (hqCity2) {
      return `${hqCity2} - USA`
    }
    return undefined
  }

  const renderPredictedTraffic = (group: string) => {
    const badges = group.split(',').map((traffic) => traffic.trim())
    return (
      <div className="flex gap-1">
        {badges.map((badge, index) => (
          <PredictedTrafficBadge key={index} value={badge} size="small" />
        ))}
      </div>
    )
  }

  const getInstagramHandle = (url: string) => {
    try {
      const match = url.match(/instagram\.com\/([^/?]+)/)
      return match ? `@${match[1]}` : url
    } catch {
      return url
    }
  }

  const renderInstagramRanking = (textPercentile: string | null) => {
    return <PredictedTrafficBadge value={textPercentile} size="small" />
  }

  // Build stats for summary tab
  const stats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Cuisine',
      value: chainData?.cuisine_50 || '-',
    },
    {
      label: 'Predicted Traffic',
      value: chainData?.velocity_group
        ? renderPredictedTraffic(chainData.velocity_group)
        : '-',
    },
    {
      label: 'Expense Category',
      value: chainData?.expense_category ? (
        <span className="font-medium text-green-700">
          {chainData.expense_category}
        </span>
      ) : (
        '-'
      ),
    },
    {
      label: 'Average Rating',
      value: chainData?.rating ? <Rating value={chainData.rating} /> : '-',
    },
    {
      label: 'Total Reviews',
      value: chainData?.reviews_count
        ? formatInteger(chainData.reviews_count)
        : '0',
    },
  ]

  // Build Instagram stats
  const instagramStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Instagram Handle',
      value: chainData?.instagram?.url ? (
        <AFbLink
          className="!text-xs"
          href={chainData.instagram.url}
          target="_blank"
        >
          {getInstagramHandle(chainData.instagram.url)}
        </AFbLink>
      ) : (
        '-'
      ),
    },
    {
      label: 'Instagram Posts',
      value: chainData?.instagram?.posts
        ? formatInteger(chainData.instagram.posts)
        : '0',
    },
    {
      label: 'Instagram Ranking',
      value: chainData?.instagram?.text_percentile
        ? renderInstagramRanking(chainData.instagram.text_percentile)
        : '-',
    },
    {
      label: 'Instagram Followers',
      value: chainData?.instagram?.followers
        ? formatInteger(chainData.instagram.followers)
        : '0',
    },
  ]

  // Build characteristics
  const characteristics = [
    ...(chainData?.services || []),
    ...(chainData?.atmospheres || []),
    ...(chainData?.offerings || []),
    ...(chainData?.open_hours || []),
  ].filter(Boolean)

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = [
    <DescriptionAndContactInfo
      key="description-contact"
      description={chainData?.description}
    />,
    <div key="metrics" className="grid grid-cols-2 gap-3">
      <MetricCard
        key="chain-size"
        value={formatInteger(chainData?.count || 0)}
        label="Chain Size"
        actionText="View Doors"
        onActionClick={() => navigate(`/chain/${urlParams?.chain_name}/doors`)}
      />
      <MetricCard
        key="menu-size"
        value={formatInteger(chainData?.menu_length || 0)}
        label="Chain Menu Size"
        actionText="View Menu"
        onActionClick={() =>
          navigate(
            `/menu?chain_id=${chainData?.id}&chain_name=${encodeURIComponent(chainData?.chain || '')}`
          )
        }
      />
    </div>,
    <StatsCard key="stats" stats={stats} />,
    <Divider key="divider-2" />,
    <StatsCard key="instagram-stats" stats={instagramStats} />,
    <Divider key="divider-3" />,
    <ForecastingSection
      key="forecasting"
      chainId={chainData?.contact_company_id}
    />,
    <Divider key="divider-4" />,
    <AdditionalCharacteristics
      key="characteristics"
      characteristics={characteristics}
    />,
  ]

  // Build location details tab content
  const locationDetailsTabContent: React.ReactNode[] = [
    <MetricCard
      key="hq-city"
      value={formatHqCity() || 'N/A'}
      label="HQ City"
      variant="full-width"
    />,
    <Divider key="divider-2" />,
    <div key="summaries" className="space-y-9">
      <SectionWithContent
        key="city-summary"
        title="City Summary"
        content={chainData?.city_summary || 'N/A'}
      />
      <SectionWithContent
        key="state-summary"
        title="State Summary"
        content={chainData?.state_summary || 'N/A'}
      />
      <SectionWithContent
        key="zip-summary"
        title="Zip Summary"
        content={chainData?.zip_summary || 'N/A'}
      />
      <SectionWithContent
        key="dma-summary"
        title="DMA Summary"
        content={chainData?.dma_summary || 'N/A'}
      />
    </div>,
  ]

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!chainData) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Chain not found</p>
        </div>
      </PageContainer>
    )
  }

  return (
    <>
      <PageContainer>
        {/* Back Button */}
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>

        {/* Company Header */}
        <CompanyHeaderSection>
          {/* Logo and Name Row */}
          <CompanyHeaderRow>
            <CompanyLogoContainer>
              {chainData.logo ? (
                <CompanyLogo src={chainData.logo} alt={chainData.chain || ''} />
              ) : (
                <EmptyLogo />
              )}
            </CompanyLogoContainer>
            <CompanyName>{chainData.chain}</CompanyName>
          </CompanyHeaderRow>

          {/* Meta Row: Locations, Updated Date, Social Icons */}
          <CompanyMetaRow>
            {chainData.count && (
              <span>{formatInteger(chainData.count)} locations</span>
            )}
            {modifiedDate && (
              <>
                <MetaDivider>•</MetaDivider>
                <span>Updated {formatDate2(modifiedDate)}</span>
              </>
            )}
            {chainData.domain && (
              <>
                <MetaDivider>•</MetaDivider>
                <SocialLink
                  href={
                    chainData.domain.startsWith('http')
                      ? chainData.domain
                      : `https://${chainData.domain}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Visit website"
                >
                  <LinkIcon />
                </SocialLink>
              </>
            )}
            {chainData.instagram?.url && (
              <SocialLink
                href={chainData.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                title="View Instagram"
              >
                <FaInstagram />
              </SocialLink>
            )}
          </CompanyMetaRow>
        </CompanyHeaderSection>

        {/* Action Buttons */}
        <ActionButtonsRow>
          <ActionButton
            onClick={() =>
              navigate(
                // This is nested inside an index file
                `/contacts?contact_company_id=${chainData.contact_company_id}`
              )
            }
          >
            <PiIdentificationCard />
            <ActionCount>({companyData?.contact_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/deals?contact_company_id=${chainData.contact_company_id}`
              )
            }
          >
            <PiHandshake />
            <ActionCount>({companyData?.deal_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/notes?contact_company_id=${chainData.contact_company_id}&company_name=${encodeURIComponent(chainData.chain || '')}`
              )
            }
          >
            <PiNotepad />
            <ActionCount>({companyData?.note_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/reminders?contact_company_id=${chainData.contact_company_id}`
              )
            }
          >
            <PiCalendarStar />
            <ActionCount>({0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/samples?contact_company_id=${chainData.contact_company_id}&company_name=${encodeURIComponent(chainData.chain || '')}`
              )
            }
          >
            <PiHandCoins />
            <ActionCount>({0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/chain/${urlParams?.chain_name}/emails?contact_company_id=${chainData.contact_company_id}`
              )
            }
          >
            <PiEnvelope />
            <ActionCount>({companyData?.email_count || 0})</ActionCount>
          </ActionButton>
        </ActionButtonsRow>

        {/* Account Attributes Section */}
        <ContentSection>
          <MobileAccountAttributesSummary
            chainProxyId={chainData.contact_company_id!}
            chainName={urlParams?.chain_name || ''}
          />
        </ContentSection>

        <SectionDivider />

        {/* Deals Overview Section */}
        <ContentSection>
          <MobileDealsAccordion chainProxyId={chainData.contact_company_id!} />
        </ContentSection>

        <SectionDivider />

        {/* Reminders Section */}
        <ContentSection>
          <MobileRemindersAccordion
            chainProxyId={chainData.contact_company_id!}
          />
        </ContentSection>

        <SectionDivider />

        {/* Event History Section */}
        <ContentSection>
          <MobileEventHistoryAccordion
            chainProxyId={chainData.contact_company_id!}
          />
        </ContentSection>
      </PageContainer>

      {/* First Bite Data Drawer - Outside PageContainer for fixed positioning */}
      <FirstBiteDataDrawer
        chainID={chainID}
        chainProxyId={chainData.contact_company_id}
        chainName={chainData.chain}
        logo={chainData.logo}
        summaryContent={summaryTabContent}
        locationDetailsContent={locationDetailsTabContent}
      />
    </>
  )
}
